import {
    AssociateModeRelations,
    ASSOCIATION_MODE,
    AssociationDialogProps,
    PAIRING_MODE,
    Profile,
} from '@/types/pages/storeManagement/device/associate';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BaseDialog from '../ui/BaseDialog';
import DefaultLayoutDialog from './DefaultLayoutDialog';
import ImageDialog from './ImageDialog';
import PlanogramDialog from './PlanogramDialog';
import SingleAssociationContent from './SingleAssociationContent';
import MultiAssociationContent from './MultiAssociationContent';
import { useAssociationHook } from './provider/useAssociationHook';
import { useAssociationState } from './provider/context';
import { filterLayoutsByPairingMode, formatLayoutOptions } from '@/utils/associateUtils';
import { useCallback, useMemo } from 'react';
import { isEmpty } from 'lodash';

const AssociateDialog = ({ mode, onClose, macs, onUpdateSuccess }: AssociationDialogProps) => {
    const { t } = useTranslation('common');
    const { state, dispatch } = useAssociationState();
    const { initialize, handleNext, handleAssociateSingle, handleAssociateMulti } = useAssociationHook({
        onUpdateSuccess,
    });

    const {
        loading,
        profiles,
        productLoading,
        noCampaignWording,
        associateLoading,
        currentProfile,
        dialogs,
        shelfInfo,
        imageInfo,
        selectedPairingMode,
        multiAssociateTemp,
        singleAssociateTemp,
    } = state;
    const isSingle = mode === ASSOCIATION_MODE.SINGLE;
    const dialogTitle = isSingle ? 'Single Association' : 'Multi Association';

    const disableConfirm = useMemo(() => {
        if (isEmpty(profiles)) return true;

        if (isSingle) {
            const availableProfileCount = Object.values(profiles).filter((profile) => {
                const availableLayouts = filterLayoutsByPairingMode(profile.layouts || [], selectedPairingMode);
                return availableLayouts.length > 0;
            }).length;

            return !singleAssociateTemp.isFinish || !singleAssociateTemp.product || availableProfileCount === 0;
        }
        return Object.values(multiAssociateTemp).some((temp) => !temp.isFinish);
    }, [isSingle, profiles, selectedPairingMode, multiAssociateTemp, singleAssociateTemp]);

    const layoutOptions = useMemo(() => {
        if (currentProfile) {
            if (isSingle) {
                const singleLayouts = currentProfile.layouts || [];
                return formatLayoutOptions(filterLayoutsByPairingMode(singleLayouts, selectedPairingMode));
            }

            const multiLayouts = currentProfile.subLayouts[state.multi.styleInfo.slug] || [];
            return formatLayoutOptions(filterLayoutsByPairingMode(multiLayouts, selectedPairingMode));
        }
        return [];
    }, [isSingle, currentProfile, selectedPairingMode, state.multi.styleInfo.slug]);

    const handleLayoutOnConfirm = (id: number) => {
        dispatch({ type: 'SET_DIALOG', payload: { key: 'defaultLayout', open: false } });
        if (isSingle) {
            dispatch({
                type: 'SET_SINGLE_LAYOUT_RELATIONS',
                payload: { key: (currentProfile as Profile).identifier, layoutId: id },
            });
            return;
        }
        dispatch({
            type: 'SET_MULTI_LAYOUT_RELATIONS',
            payload: id,
        });
    };

    const handlePairingModeOnConfirm = (mode: PAIRING_MODE) => async (relations: AssociateModeRelations) => {
        dispatch({ type: 'SET_DIALOG', payload: { key: mode, open: false } });
        dispatch({ type: 'SET_DIALOG', payload: { key: 'multiProfile', open: false } });
        if (isSingle) {
            await handleAssociateSingle({
                source: mode,
                ...relations,
            });
            return;
        }

        if (mode === PAIRING_MODE.SHELF) {
            dispatch({ type: 'SET_MULTI_SHELF_RELATIONS', payload: relations });
        } else {
            dispatch({ type: 'SET_MULTI_IMAGE_RELATIONS', payload: relations });
        }
    };

    const handleOnConfirm = useCallback(async () => {
        if (isSingle) {
            const product = state.singleAssociateTemp.product;
            if (!product) return;
            await handleNext(shelfInfo, imageInfo, ASSOCIATION_MODE.SINGLE);
            return;
        }
        handleAssociateMulti();
    }, [isSingle, state.singleAssociateTemp.product, shelfInfo, imageInfo]);

    return (
        <>
            <BaseDialog
                open={mode === ASSOCIATION_MODE.SINGLE || mode === ASSOCIATION_MODE.MULTI}
                dialogTitle={t(dialogTitle)}
                confirmButtonText={t('Continue')}
                cancelButtonText={''}
                disableConfirm={loading || disableConfirm || associateLoading || productLoading || !!noCampaignWording}
                showSpinnerOnDisable={associateLoading}
                showTopRightClose
                onConfirm={handleOnConfirm}
                onClose={() => {
                    if (associateLoading) return;
                    onClose();
                }}
                TransitionProps={{
                    onEnter: () => initialize(macs),
                    onExited: () => dispatch({ type: 'SET_CLEAR' }),
                }}
            >
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <CircularProgress size={50} />
                    </Box>
                ) : (
                    <Box
                        width={'100%'}
                        sx={{
                            pointerEvents: associateLoading ? 'none' : 'auto',
                            opacity: associateLoading ? 0.6 : 1,
                            userSelect: associateLoading ? 'none' : 'auto',
                        }}
                    >
                        {mode === ASSOCIATION_MODE.SINGLE && <SingleAssociationContent />}
                        {mode === ASSOCIATION_MODE.MULTI && <MultiAssociationContent />}
                    </Box>
                )}
            </BaseDialog>
            <PlanogramDialog
                open={dialogs.shelf}
                planogramInfo={shelfInfo.options}
                relations={shelfInfo.relations}
                handleOnClick={handlePairingModeOnConfirm(PAIRING_MODE.SHELF)}
                onClose={() => dispatch({ type: 'SET_DIALOG', payload: { key: PAIRING_MODE.SHELF, open: false } })}
            />
            <ImageDialog
                open={dialogs.image}
                options={imageInfo.options}
                relations={imageInfo.relations}
                handleOnClick={handlePairingModeOnConfirm(PAIRING_MODE.IMAGE)}
                onClose={() => dispatch({ type: 'SET_DIALOG', payload: { key: PAIRING_MODE.IMAGE, open: false } })}
            />
            {currentProfile && (
                <>
                    <DefaultLayoutDialog
                        open={dialogs.defaultLayout}
                        title={currentProfile.name}
                        options={layoutOptions}
                        onConfirm={handleLayoutOnConfirm}
                        onClose={() => dispatch({ type: 'SET_DIALOG', payload: { key: 'defaultLayout', open: false } })}
                    />
                </>
            )}
        </>
    );
};

export default AssociateDialog;
